/**
 * The paperwork behind a chargeback: both sides of the cheque and the
 * bank's notice.
 *
 * WHY THIS IS A DIALOG ON THE CHARGEBACK ROW. A reversal takes money off
 * a homeowner's ledger with no person involved, and the first thing the
 * homeowner asks is "show me". The cheque image is that answer; the
 * notice is the bank's own account of why — a two-page "Advice for
 * unpaid deposited item" with a legal copy of the cheque stamped with
 * the return reason. Both are reached through the chargeback's own
 * routes, which serve a stored copy when there is one and fetch live
 * from the bank when there is not, storing on the way out.
 *
 * Stored copies outlive the bank's ~90-day retention. The dialog says
 * which it is showing, because "live from the bank" is a thing that
 * stops being available and "stored" is not.
 *
 * An ACH return has no cheque, so the dialog offers only the notice.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import LoadingSpinner from './LoadingSpinner';
import { bankService } from './clients/ApiClients';
import { ChargebackReturn } from './clients/rust-api-client';
import { formatDate, getBankErrorParts } from './utils/bankHelpers';

interface ChargebackDocumentsDialogProps {
  /** The chargeback whose documents to show; `null` closes the dialog. */
  chargeback: ChargebackReturn | null;
  onClose: () => void;
}

type Tab = 'front' | 'back' | 'notice';

const isAch = (c: ChargebackReturn): boolean =>
  (c.chargeback_type ?? '').toUpperCase() === 'ACH';

/** What the row says is in our own storage, per tab. */
const isStored = (c: ChargebackReturn, tab: Tab): boolean =>
  tab === 'front'
    ? c.documents.check_front
    : tab === 'back'
      ? c.documents.check_back
      : c.documents.notice;

const ChargebackDocumentsDialog: React.FC<ChargebackDocumentsDialogProps> = ({
  chargeback,
  onClose,
}) => {
  const [tab, setTab] = useState<Tab>(() =>
    chargeback && isAch(chargeback) ? 'notice' : 'front'
  );
  const [urls, setUrls] = useState<Partial<Record<Tab, string>>>({});
  const [loading, setLoading] = useState<Tab | null>(null);
  const [error, setError] = useState<Partial<Record<Tab, string>>>({});

  const tabs = useMemo<Tab[]>(
    () =>
      chargeback && isAch(chargeback)
        ? ['notice']
        : ['front', 'back', 'notice'],
    [chargeback]
  );

  // One object URL per tab, revoked when the chargeback changes or the
  // dialog unmounts.
  useEffect(() => {
    setTab(chargeback && isAch(chargeback) ? 'notice' : 'front');
    setUrls({});
    setError({});
    return () => {
      setUrls((current) => {
        Object.values(current).forEach((u) => u && URL.revokeObjectURL(u));
        return {};
      });
    };
  }, [chargeback]);

  /**
   * The notice needs its statement id. The row carries it once the
   * document pass (or an earlier view) has listed the notice; before
   * that, one listing call finds it — and an empty listing is the bank
   * saying the notice is not generated yet, which is ordinary.
   */
  const fetchNotice = useCallback(
    async (c: ChargebackReturn): Promise<Blob> => {
      let statementId = c.documents.notice_statement_id ?? null;
      if (!statementId) {
        const listed = await bankService.listChargebackNotices({
          chargebackId: c.id,
        });
        statementId = listed.data.statements[0]?.statement_id ?? null;
      }
      if (!statementId) {
        throw new Error('no_notice_yet');
      }
      const resp = await bankService.getChargebackNoticeFile(
        { chargebackId: c.id, statementId },
        { responseType: 'blob' as const }
      );
      return resp.data as unknown as Blob;
    },
    []
  );

  useEffect(() => {
    // A tab the row does not offer — the cheque sides of an ACH return
    // between one chargeback and the next — is never fetched.
    if (!chargeback || !tabs.includes(tab)) return;
    if (urls[tab] || error[tab] || loading === tab) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(tab);
        let blob: Blob;
        if (tab === 'notice') {
          blob = await fetchNotice(chargeback);
        } else {
          const resp = await bankService.getChargebackImage(
            { chargebackId: chargeback.id, side: tab },
            { responseType: 'blob' as const }
          );
          blob = resp.data as unknown as Blob;
        }
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setUrls((current) => ({ ...current, [tab]: url }));
      } catch (e) {
        if (cancelled) return;
        if (e instanceof Error && e.message === 'no_notice_yet') {
          setError((current) => ({
            ...current,
            notice:
              'The bank has not issued a notice for this chargeback yet. It usually arrives the same day; check back later.',
          }));
          return;
        }
        const { status, body } = getBankErrorParts(e);
        const fallback =
          tab === 'notice'
            ? "Couldn't load the notice."
            : "Couldn't load the cheque image.";
        setError((current) => ({
          ...current,
          [tab]:
            status === 404
              ? body || 'The bank has no document for this.'
              : body || fallback,
        }));
      } finally {
        if (!cancelled) setLoading(null);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // `urls`, `error` and `loading` are read, not depended on: adding
    // them would re-run the effect on every state change it makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeback, tab, tabs, fetchNotice]);

  const label = (t: Tab): string =>
    t === 'front' ? 'Front' : t === 'back' ? 'Back' : 'Notice';
  const url = urls[tab];
  const title = chargeback
    ? `${isAch(chargeback) ? 'ACH return' : 'Returned cheque'}${
        chargeback.check_number ? ` #${chargeback.check_number}` : ''
      }`
    : 'Documents';

  return (
    <Dialog open={!!chargeback} onClose={onClose} size="3xl">
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>
        {chargeback?.amount ? `$${chargeback.amount}` : ''}
        {chargeback?.chargeback_date
          ? `, returned ${formatDate(chargeback.chargeback_date)}`
          : ''}
        {chargeback?.return_reason ? ` — ${chargeback.return_reason}` : ''}.
        Bank reference {chargeback?.provider_chargeback_id ?? '—'}.
      </DialogDescription>
      <DialogBody>
        <div className="mb-3 flex items-center gap-2">
          {tabs.map((t) =>
            tab === t ? (
              <Button key={t} outline onClick={() => setTab(t)}>
                {label(t)}
              </Button>
            ) : (
              <Button key={t} plain onClick={() => setTab(t)}>
                {label(t)}
              </Button>
            )
          )}
          {/* Which copy this is. A stored copy is ours for good; a live
              one exists only while the bank keeps it. */}
          {chargeback && (
            <Text className="ml-auto text-xs">
              {isStored(chargeback, tab) ? 'Stored copy' : 'Live from the bank'}
            </Text>
          )}
        </div>
        <div className="min-h-48 rounded-lg bg-stone-100 p-2 dark:bg-gray-700">
          {loading === tab && !url ? (
            <LoadingSpinner />
          ) : url && tab === 'notice' ? (
            // Not an <iframe>. The portal's Content Security Policy says
            // `frame-src 'none'` (report-only today, enforced later), and
            // framing a blob: PDF trips it — seen in levittown-725 on
            // 2026-10-21. CSP does not govern a top-level navigation, so
            // the PDF opens in its own tab instead.
            <div className="p-4 text-center">
              <Text className="mb-3 text-sm">
                The bank’s notice: an “Advice for unpaid deposited item” with a
                legal copy of the cheque and the return reason.
              </Text>
              <Button href={url} target="_blank" rel="noreferrer">
                Open the notice (PDF)
              </Button>
            </div>
          ) : url ? (
            <img
              src={url}
              alt={`${label(tab)} of the returned cheque`}
              className="mx-auto max-h-[70vh] w-auto"
            />
          ) : (
            <Text className="p-4 text-sm">{error[tab] ?? 'No document.'}</Text>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChargebackDocumentsDialog;
